import { useState, useEffect, useRef } from 'react';
import { DateSmall, DateLarge, Label } from './styled';
import { Textfield } from '@simplywallst/ui-core';
import InvalidMessage from '@components/InvalidMessage.deprecated';

interface Props {
  name: string;
  error?: string;
  onChange: (date: string) => void;
}

type Type = 'day' | 'month' | 'year';

const now = new Date();
const numeric = /^$|\d+$/;
const yearStart = '1970';
const yearStop = now.getFullYear().toString();

const computeDay = (value: string) => {
  let day = value;
  const dayNum = Number(day);
  if (day.length === 1 && dayNum > 3) day = `0${day}`;
  if (day.length === 2 && dayNum > 31) day = '31';
  return day;
};

const computeMonth = (value: string) => {
  let month = value;
  const monthNum = Number(month);
  if (month.length === 1 && monthNum > 1) month = `0${month}`;
  if (month.length === 2 && monthNum > 12) month = '12';
  return month;
};

const computeYear = (value: string) => {
  let year = value;
  const yearNum = Number(year);
  const yStart = Number(yearStart.slice(0, year.length));
  const yStop = Number(yearStop.slice(0, year.length));
  if (yearNum < yStart) year = `${yStart}`;
  if (yearNum > yStop) year = `${yStop}`;
  return year;
};

const DateField = ({ error, name, onChange }: Props) => {
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const dayRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (day && day.length === 2) {
      const { current } = monthRef;
      if (current) {
        current.focus();
      }
    }
  }, [day]);

  useEffect(() => {
    if (month && month.length === 2) {
      const { current } = yearRef;
      if (current) {
        current.focus();
      }
    }
  }, [month]);

  const handleFocus = (ref) => {
    const { current } = ref;
    if (current) {
      current.select();
    }
  };

  const handleChange = (e, type: Type) => {
    if (numeric.test(e.currentTarget.value)) {
      let ISO8601 = '';
      let nextDay = day;
      let nextMonth = month;
      let nextYear = year;
      if (type === 'day') {
        nextDay = computeDay(e.currentTarget.value);
        ISO8601 = `${year}-${month}-${nextDay}`;
      } else if (type === 'month') {
        nextMonth = computeMonth(e.currentTarget.value);
        ISO8601 = `${year}-${nextMonth}-${day}`;
      } else if (type === 'year') {
        nextYear = computeYear(e.currentTarget.value);
        ISO8601 = `${nextYear}-${month}-${day}`;
      }
      setDay(nextDay);
      setMonth(nextMonth);
      setYear(nextYear);
      onChange(ISO8601);
    }
  };

  const handleBlur = (value, type: Type) => {
    let ISO8601 = '';
    let nextDay = day;
    let nextMonth = month;
    let nextYear = year;
    if (type === 'day') {
      nextDay = value;
      ISO8601 = `${year}-${month}-${nextDay}`;
    } else if (type === 'month') {
      nextMonth = value;
      ISO8601 = `${year}-${nextMonth}-${day}`;
    } else if (type === 'year') {
      nextYear = value;
      ISO8601 = `${nextYear}-${month}-${day}`;
    }
    setDay(nextDay);
    setMonth(nextMonth);
    setYear(nextYear);
    onChange(ISO8601);
  };

  return (
    <div>
      <Label>Transaction Date</Label>
      <div>
        <DateSmall error={error}>
          <Textfield
            styleType="standardReverse"
            ref={dayRef}
            name="day"
            type="text"
            placeholder="dd"
            onBlur={(e) => {
              if (e.currentTarget.value.length === 1) {
                handleBlur(`0${e.currentTarget.value}`, 'day');
              }
            }}
            onFocus={() => handleFocus(dayRef)}
            onChange={(e) => handleChange(e, 'day')}
            value={day}
            maxLength={2}
          />
        </DateSmall>
        <DateSmall error={error}>
          <Textfield
            styleType="standardReverse"
            ref={monthRef}
            name="month"
            type="text"
            placeholder="mm"
            onBlur={(e) => {
              if (e.currentTarget.value.length === 1) {
                handleBlur(`0${e.currentTarget.value}`, 'month');
              }
            }}
            onFocus={() => handleFocus(monthRef)}
            onChange={(e) => handleChange(e, 'month')}
            value={month}
            maxLength={2}
          />
        </DateSmall>
        <DateLarge error={error}>
          <Textfield
            styleType="standardReverse"
            ref={yearRef}
            name="year"
            type="text"
            placeholder="yyyy"
            onBlur={(e) => {
              if (e.currentTarget.value.length !== 4) {
                handleBlur('', 'year');
              }
            }}
            onFocus={() => handleFocus(yearRef)}
            onChange={(e) => handleChange(e, 'year')}
            value={year}
            maxLength={4}
          />
        </DateLarge>
      </div>
      {error && (
        <InvalidMessage name={`${name}-invalid-message`} message={error} />
      )}
    </div>
  );
};

export default DateField;
